import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, BookOpen, TrendingUp, Star, Clock, ChevronRight, Sparkles } from 'lucide-react';
import ArticleCard from '../../components/academy/ArticleCard';
import CategoryGrid from '../../components/academy/CategoryGrid';

const API = process.env.REACT_APP_BACKEND_URL;

const AcademyHome = () => {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, latRes, catRes] = await Promise.all([
          fetch(`${API}/api/academy/articles/featured`),
          fetch(`${API}/api/academy/articles/latest?limit=8`),
          fetch(`${API}/api/academy/categories`),
        ]);
        const [featData, latData, catData] = await Promise.all([
          featRes.json(), latRes.json(), catRes.json(),
        ]);
        setFeatured(featData.articles || []);
        setLatest(latData.articles || []);
        setCategories(catData.categories || []);
      } catch (e) {
        console.error('Failed to load academy data:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      window.location.href = `/academy/search?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#115E59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 pt-8 pb-20 md:pb-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(17,94,89,0.25)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(30,58,138,0.15)_0%,_transparent_60%)]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.07] backdrop-blur-md border border-white/10 rounded-full px-4 py-1.5 mb-8">
            <BookOpen className="h-4 w-4 text-teal-400" />
            <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Quidity Academy</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            The Commercial Finance<br />
            <span style={{ background: 'linear-gradient(135deg, #5eead4 0%, #3b82f6 50%, #a78bfa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Knowledge Center
            </span>
          </h1>

          <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            Expert guides, in depth articles, and practical resources for business owners navigating commercial finance, SBA loans, real estate capital, and business growth.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto relative" data-testid="academy-search-form">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              data-testid="academy-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles or ask a question..."
              className="w-full bg-white/[0.08] backdrop-blur-sm border border-white/15 text-white rounded-full pl-14 pr-52 py-4 placeholder:text-slate-500 focus:ring-2 focus:ring-teal-500/40 focus:border-transparent transition-all text-sm"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1.5">
              <button
                type="submit"
                data-testid="academy-search-button"
                className="bg-white/10 border border-white/15 text-white text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-white/20 transition-colors"
              >
                Search
              </button>
              <button
                type="button"
                data-testid="academy-ask-button"
                onClick={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim().length >= 5) {
                    window.location.href = `/academy/search?q=${encodeURIComponent(searchQuery.trim())}&mode=ask`;
                  }
                }}
                className="bg-[#115E59] text-white text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-[#0d4f4a] transition-colors flex items-center gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" /> Ask Quidity
            </button>
          </div>
          </form>
        </div>
      </section>

      {/* Featured / Cornerstone Articles */}
      {featured.length > 0 && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#115E59]">Featured Guides</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  Comprehensive Resources
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 6).map((article) => (
                <ArticleCard key={article.slug} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Browse by Category */}
      {categories.length > 0 && (
        <section className="py-16 md:py-20 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#115E59] mb-3 block">Browse Topics</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Explore by Category
              </h2>
            </div>
            <CategoryGrid categories={categories} />
          </div>
        </section>
      )}

      {/* Latest Articles */}
      {latest.length > 0 && (
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-[#115E59]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#115E59]">Latest</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  Recent Articles
                </h2>
              </div>
              <Link
                to="/academy/search?q="
                data-testid="view-all-articles"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#115E59] hover:underline"
              >
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {latest.slice(0, 8).map((article) => (
                <ArticleCard key={article.slug} article={article} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-20 md:py-24 overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(17,94,89,0.2)_0%,_transparent_70%)]" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Put This Knowledge to Work?
          </h2>
          <p className="text-base text-slate-400 mb-8 max-w-xl mx-auto">
            Quidity provides the financing solutions discussed throughout our Academy. From SBA loans to equipment financing, we are ready to help your business grow.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/apply"
              data-testid="academy-cta-apply"
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_30px_rgba(94,234,212,0.3)] transition-all"
            >
              Apply for Financing <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white/[0.07] border border-white/10 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/[0.12] transition-all"
            >
              Talk to an Advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcademyHome;
